import React, { useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Product } from "module/types";
import ProductGameHeader from "../ProductGameHeader";
import SEO from "components/SEO";
import AIService from "api/aiService";
import { FightSimulationResult } from "network/AIProtocol";
import FighterForm from "./components/FighterForm";
import ResultView from "./components/ResultView";
import {
  fightSimulatorReducer,
  initialFightSimulatorState,
  canStartSimulation,
  Fighter,
  FightResult,
} from "./FightSimulatorReducer";

interface Props {
  product: Product;
}

const FightSimulatorView: React.FC<Props> = ({ product }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(
    fightSimulatorReducer,
    initialFightSimulatorState
  );
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isSettingOpen, setIsSettingOpen] = useState(false);

  const { phase, fighterA, fighterB, error, result } = state;

  // AI 프롬프트 생성
  const buildFightPrompt = (a: Fighter, b: Fighter): string => {
    const formatMartialArts = (fighter: Fighter) => {
      if (fighter.martialArts.length === 0) return "없음";
      return fighter.martialArts
        .map((m) => `${m.type} ${m.durationMonths}개월`)
        .join(", ");
    };

    const formatFitnessLevel = (level: string) => {
      switch (level) {
        case "high":
          return "상";
        case "medium":
          return "중";
        case "low":
          return "하";
        default:
          return level;
      }
    };

    const formatHealthInfo = (fighter: Fighter) => {
      if (!fighter.health.hasHealthExperience) return "없음";
      return `${fighter.health.yearsOfTraining}년 경력 (벤치: ${fighter.health.benchPress}kg, 데드: ${fighter.health.deadlift}kg, 스쿼트: ${fighter.health.squat}kg)`;
    };

    const formatCardioInfo = (fighter: Fighter) => {
      if (!fighter.cardio.hasCardioExperience) return "없음";
      return `최대 ${fighter.cardio.maxRunningDistance}km 주행`;
    };

    return `당신은 격투 전문가이자 소설가입니다. 두 사람의 정보를 분석하여 가상의 싸움 결과를 예측하고, 생생한 싸움 스토리를 작성해주세요.

## Fighter A: ${a.name}
- 신체: ${a.height}cm, ${a.weight}kg, ${a.age}세, ${
      a.gender === "male" ? "남성" : "여성"
    }
- 격투 경력: ${formatMartialArts(a)}
- 체력 수준: ${formatFitnessLevel(a.fitnessLevel)}
- 실전 경험: ${a.hasRealFightExperience ? "있음" : "없음"}
- 헬스 경력: ${formatHealthInfo(a)}
- 유산소 능력: ${formatCardioInfo(a)}

## Fighter B: ${b.name}
- 신체: ${b.height}cm, ${b.weight}kg, ${b.age}세, ${
      b.gender === "male" ? "남성" : "여성"
    }
- 격투 경력: ${formatMartialArts(b)}
- 체력 수준: ${formatFitnessLevel(b.fitnessLevel)}
- 실전 경험: ${b.hasRealFightExperience ? "있음" : "없음"}
- 헬스 경력: ${formatHealthInfo(b)}
- 유산소 능력: ${formatCardioInfo(b)}

## 요청
1. 두 사람의 신체 조건, 격투 경력, 헬스 경력, 유산소 능력을 분석하여 승률을 예측하세요.
2. 실제로 싸움이 벌어졌다고 가정하고, 생생한 싸움 스토리를 3-5문장으로 작성하세요.
   - 구체적인 기술 이름 사용 (잽, 어퍼컷, 암바, 테이크다운 등)
   - 각 fighter의 이름(${a.name}, ${b.name})을 사용하세요
   - 역동적이고 긴장감 있는 묘사
   - 헬스 경력이나 유산소 능력이 있으면 이를 반영하세요

반드시 다음 JSON 형식으로만 응답하세요:
{
  "winProbabilityA": 0-100 사이의 숫자 (Fighter A의 승률),
  "story": "싸움 스토리 (3-5문장)",
  "keyFactors": ["승부 요인 1", "승부 요인 2", "승부 요인 3"]
}`;
  };

  // 시뮬레이션 실행
  const handleSimulate = async () => {
    dispatch({ type: "START_SIMULATION" });

    try {
      const prompt = buildFightPrompt(fighterA, fighterB);
      const response =
        await AIService.getInstance().request<FightSimulationResult>(
          "fight_simulation",
          prompt,
          { fighterA, fighterB }
        );

      if (response.success && response.data) {
        const fightResult: FightResult = {
          winProbabilityA: response.data.winProbabilityA,
          story: response.data.story,
          keyFactors: response.data.keyFactors,
        };
        dispatch({ type: "SIMULATION_COMPLETE", payload: fightResult });
      } else {
        dispatch({
          type: "SIMULATION_ERROR",
          payload: response.error || t("FightSimulator.errorGeneric"),
        });
      }
    } catch (err) {
      dispatch({
        type: "SIMULATION_ERROR",
        payload: t("FightSimulator.errorGeneric"),
      });
    }
  };

  // 다시 시뮬레이션
  const handleRetry = () => {
    dispatch({ type: "RETRY" });
    handleSimulate();
  };

  // 새로운 매치
  const handleNewMatch = () => {
    dispatch({ type: "NEW_MATCH" });
  };

  // 홈으로 이동
  const handleLeaveRoom = () => {
    navigate("/", { replace: true });
  };

  // 입력 화면 렌더링
  const renderInputPhase = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="max-w-4xl mx-auto">
        {/* VS 헤더 */}
        <div className="text-center mb-6">
          <span className="text-4xl font-bold text-yellow-400">VS</span>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-center">
            {error}
          </div>
        )}

        {/* Fighter 입력 폼 (2열 또는 스택) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <FighterForm
            fighter={fighterA}
            label="Fighter A"
            color="red"
            onUpdate={(updates) =>
              dispatch({ type: "UPDATE_FIGHTER_A", payload: updates })
            }
            onAddMartialArt={(ma) =>
              dispatch({ type: "ADD_MARTIAL_ART_A", payload: ma })
            }
            onRemoveMartialArt={(id) =>
              dispatch({ type: "REMOVE_MARTIAL_ART_A", payload: id })
            }
          />
          <FighterForm
            fighter={fighterB}
            label="Fighter B"
            color="blue"
            onUpdate={(updates) =>
              dispatch({ type: "UPDATE_FIGHTER_B", payload: updates })
            }
            onAddMartialArt={(ma) =>
              dispatch({ type: "ADD_MARTIAL_ART_B", payload: ma })
            }
            onRemoveMartialArt={(id) =>
              dispatch({ type: "REMOVE_MARTIAL_ART_B", payload: id })
            }
          />
        </div>

        {/* 시뮬레이션 시작 버튼 */}
        <div className="text-center">
          <button
            onClick={handleSimulate}
            disabled={!canStartSimulation(state)}
            className={`px-8 py-4 text-xl font-bold rounded-xl transition-all ${
              canStartSimulation(state)
                ? "bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                : "bg-gray-600 text-gray-400 cursor-not-allowed"
            }`}
          >
            🥊 {t("FightSimulator.startSimulation")}
          </button>
        </div>
      </div>
    </div>
  );

  // 시뮬레이션 중 화면 렌더링
  const renderSimulatingPhase = () => (
    <div className="flex-1 flex flex-col items-center justify-center p-4">
      <div className="text-center">
        {/* 애니메이션 */}
        <div className="text-8xl mb-6 animate-bounce">🥊</div>
        <div className="text-6xl mb-8 animate-pulse">VS</div>
        <div
          className="text-8xl mb-6 animate-bounce"
          style={{ animationDelay: "0.1s" }}
        >
          🥊
        </div>

        {/* 로딩 텍스트 */}
        <p className="text-xl text-gray-300 animate-pulse">
          {t("FightSimulator.simulating")}
        </p>
      </div>
    </div>
  );

  // 결과 화면 렌더링
  const renderResultPhase = () => {
    if (!result) return null;
    return (
      <div className="flex-1 overflow-y-auto">
        <ResultView
          fighterA={fighterA}
          fighterB={fighterB}
          result={result}
          onRetry={handleRetry}
          onNewMatch={handleNewMatch}
        />
      </div>
    );
  };

  return (
    <>
      <SEO
        title={`${product.title} - Lingple`}
        description={
          i18n.language === "ko"
            ? "AI가 두 사람의 싸움 결과를 예측합니다."
            : "AI predicts the outcome of a fight between two people."
        }
        keywords={
          i18n.language === "ko"
            ? "야차, 시뮬레이션, 싸움 예측, AI, 격투"
            : "fight, simulation, battle prediction, AI, martial arts"
        }
      />
      <div className="space-gray-theme flex flex-col h-screen bg-gray-900">
        <ProductGameHeader
          product={product}
          isRuleOpen={isRuleOpen}
          setIsRuleOpen={setIsRuleOpen}
          isSettingOpen={isSettingOpen}
          setIsSettingOpen={setIsSettingOpen}
          t={t}
        />

        {/* Setting 메뉴 */}
        {isSettingOpen && (
          <div className="fixed right-0 top-[48px] mt-2 w-40 bg-gray-800 rounded-lg shadow-xl z-40 border border-gray-700">
            <ul className="py-1">
              <li
                className="px-4 py-2 hover:bg-gray-700 cursor-pointer text-red-400 hover:text-white transition-colors"
                onClick={handleLeaveRoom}
              >
                <i className="fas fa-sign-out-alt mr-2" />
                {t("leaveRoom")}
              </li>
            </ul>
          </div>
        )}

        <div className="flex-1 flex flex-col min-h-0 pt-[48px]">
          {phase === "input" && renderInputPhase()}
          {phase === "simulating" && renderSimulatingPhase()}
          {phase === "result" && renderResultPhase()}
        </div>
      </div>
    </>
  );
};

export default FightSimulatorView;
